import * as React from 'react'
import clsx from 'clsx'
import { SxProps, sx as sxFunc } from '../sx'
import { ListContext } from './ListContext'
import style from './index.module.scss'

export interface ListClasses {
  /** Styles applied to the root element. */
  root: string
}

export interface ListProps extends React.HTMLAttributes<HTMLUListElement> {
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default ul
   */
  Component?: React.ElementType
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<ListClasses>
  /**
   * The size of the component.
   * `medium` is equivalent to the dense styling.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

export const List = React.forwardRef<HTMLUListElement, ListProps>(
  function List(props, ref) {
    const {
      children,
      classes,
      className,
      Component = 'ul',
      size = 'medium',
      sx,
      ...other
    } = props

    const context = React.useMemo(() => ({ size }), [size])

    return (
      <ListContext.Provider value={context}>
        <Component
          className={clsx(style.root, classes?.root, className, sxFunc(sx))}
          ref={ref}
          {...other}
        >
          {children}
        </Component>
      </ListContext.Provider>
    )
  }
)
